import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UsersThree, MagnifyingGlass, ArrowClockwise } from "@phosphor-icons/react";
import ManageAgentsDialog from "@/components/ManageAgentsDialog";

/**
 * v1.27.90 — Cross-party Agents page for Company Admin.
 * Read-only listing + drill-down to the same Manage Agents dialog used
 * inside Administration → Parties, so admins can see every agent across
 * every party at a glance.
 */
export default function PartyAgents() {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState("");
    const [openParty, setOpenParty] = useState(null); // { id, name }

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/super/party-agents");
            setRows(data.agents || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return rows;
        return rows.filter((a) => [a.name, a.email, a.phone, a.party_name].some((v) => (v || "").toLowerCase().includes(term)));
    }, [rows, q]);

    const byParty = useMemo(() => {
        const g = new Map();
        for (const a of filtered) {
            const key = a.party_id || "";
            if (!g.has(key)) g.set(key, { party_id: a.party_id, party_name: a.party_name || "Unknown", agents: [] });
            g.get(key).agents.push(a);
        }
        return Array.from(g.values()).sort((a, b) => (a.party_name || "").localeCompare(b.party_name || ""));
    }, [filtered]);

    return (
        <div className="space-y-5" data-testid="party-agents-page">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Administration</div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900">Party Agents</h1>
                    <p className="mt-2 text-sm text-slate-500">Every sub-user (agent) linked to every party in your company. Click a party header to manage its agents.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative">
                        <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                        <Input data-testid="party-agents-search" placeholder="Search agent or party…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 text-base" />
                    </div>
                    <Button variant="outline" onClick={load} disabled={busy} className="border-slate-300"><ArrowClockwise size={14} className="mr-1" /> Refresh</Button>
                </div>
            </div>

            {byParty.length === 0 && (
                <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
                    <UsersThree size={28} className="mx-auto mb-2 text-slate-400" />
                    No agents linked yet. Open <b>Administration → Parties</b> and use the row menu → <b>Manage Agents</b>.
                </div>
            )}

            {byParty.map((g) => (
                <div key={g.party_id} data-testid={`party-agents-group-${g.party_id}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2">
                        <div className="font-display text-base font-bold text-slate-900">{g.party_name}</div>
                        <Button size="sm" variant="outline" onClick={() => setOpenParty({ id: g.party_id, name: g.party_name })} data-testid={`manage-agents-${g.party_id}`}>
                            Manage
                        </Button>
                    </div>
                    <ul className="divide-y divide-slate-100">
                        {g.agents.map((a) => (
                            <li key={a.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-slate-900">{a.name}</span>
                                        {a.active === false && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-rose-700">Deactivated</span>}
                                    </div>
                                    <div className="text-xs text-slate-500">{a.email || "—"} · {a.phone || "—"}</div>
                                </div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-400">Agent</div>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}

            <ManageAgentsDialog
                open={!!openParty}
                onOpenChange={(v) => { if (!v) { setOpenParty(null); load(); } }}
                party={openParty}
            />
        </div>
    );
}
